import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Link,
  mergeClasses,
  Spinner,
  Tab,
  TabList,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add12Regular,
  Add16Regular,
  ArrowDownload16Regular,
  ArrowUpload16Regular,
  Chat16Regular,
  Chat20Regular,
  ChatRegular,
  Checkmark12Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Dismiss20Regular,
  DocumentFolder16Regular,
  DocumentText16Regular,
  DocumentText20Regular,
} from "@fluentui/react-icons";
import { addWorkspaceLibraryDocument, getConversationsWithCount } from "api";
import { ALLOWED_FILE_EXTENSIONS, ConstantValues } from "common/constants";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "react-query";
import { useDispatch } from "react-redux";
import {
  downloadFile,
  downloadWorkspaceLibraryDocument,
  uploadFilesToStorage,
} from "utils/blobUtils";
import { formatDate } from "utils/generalUtils";
import { IConversation } from "../../../models/IConversation";
import { ITag } from "../../../models/ITag";
import { showToast } from "../../../redux/toastActions";
import {
  useSharedBadgeStyles,
  useSharedButtonStyles,
  useSharedCardStyles,
  useSharedCloseButtonStyles,
  useSharedModalStyles,
  useSharedSectionStyles,
  useSharedTagStyles,
  useSharedTextStyles,
} from "../../../theme/sharedStyles";
import { IWorkspaceLibraryDocument } from "../../manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { useManageContentStyles } from "./ManageContent.styles";
import {
  IKnowledgeComponentGroup,
  IManageContentProps,
  ITabContentProps,
  MANAGE_CONTENT_TABS,
} from "./ManageContent.types";

const MAX_REFERENCED_CONVERSATIONS = 3;

const groupKnowledgeComponentsByCategory = (
  knowledgeComponents: ITag[],
): IKnowledgeComponentGroup[] => {
  const groups = knowledgeComponents.reduce(
    (acc, item) => {
      const categoryName = item.category?.title || "Uncategorized";
      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }
      acc[categoryName].push(item);
      return acc;
    },
    {} as Record<string, ITag[]>,
  );

  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([categoryName, items]) => ({
      categoryName,
      items: items.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    }));
};

const KnowledgeComponentsTab: React.FC<ITabContentProps> = ({
  knowledgeComponents,
  selectedKnowledgeComponents,
  onKnowledgeComponentsChange,
  isLoadingKnowledgeComponents,
}) => {
  const styles = useManageContentStyles();
  const sharedTagStyles = useSharedTagStyles();
  const badgeStyles = useSharedBadgeStyles();
  const sectionStyles = useSharedSectionStyles();
  const closeButtonStyles = useSharedCloseButtonStyles();
  const groupedComponents = useMemo(
    () => groupKnowledgeComponentsByCategory(knowledgeComponents),
    [knowledgeComponents],
  );

  const handleToggleKnowledgeComponent = useCallback(
    (component: ITag) => {
      const isSelected = selectedKnowledgeComponents.some(
        (selected) => selected.id === component.id,
      );

      if (isSelected) {
        onKnowledgeComponentsChange(
          selectedKnowledgeComponents.filter(
            (selected) => selected.id !== component.id,
          ),
        );
      } else {
        onKnowledgeComponentsChange([
          ...selectedKnowledgeComponents,
          component,
        ]);
      }
    },
    [selectedKnowledgeComponents, onKnowledgeComponentsChange],
  );

  const handleRemoveKnowledgeComponent = useCallback(
    (componentId: string | undefined) => {
      if (!componentId) return;
      onKnowledgeComponentsChange(
        selectedKnowledgeComponents.filter((comp) => comp.id !== componentId),
      );
    },
    [selectedKnowledgeComponents, onKnowledgeComponentsChange],
  );

  return (
    <>
      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Selected Knowledge Components
          </h3>
        </div>
        <div className={styles.selectedItemsContainer}>
          {selectedKnowledgeComponents.length === 0 ? (
            <span className={styles.emptySelectedState}>
              No knowledge components selected
            </span>
          ) : (
            selectedKnowledgeComponents.map((component) => (
              <div
                key={component.id}
                className={mergeClasses(
                  sharedTagStyles.tag,
                  sharedTagStyles.tagAccent,
                )}
              >
                <DocumentText16Regular className={styles.selectedTagIcon} />
                <span className={styles.tagNameWithAttachments}>
                  {component.name}
                  {component.attachments &&
                    component.attachments.length > 0 && (
                      <Tooltip
                        content="Download attachments"
                        relationship="label"
                      >
                        <Link
                          onClick={(e) => {
                            e.stopPropagation();
                            component.attachments?.forEach((file) => {
                              if (file.url) {
                                downloadFile(file.url)();
                              }
                            });
                          }}
                          aria-label={`Download attachments for ${component.name}`}
                          className={styles.downloadLink}
                        >
                          <ArrowDownload16Regular />
                        </Link>
                      </Tooltip>
                    )}
                </span>
                <button
                  className={mergeClasses(
                    closeButtonStyles.closeButton,
                    styles.removeButton,
                  )}
                  onClick={() => handleRemoveKnowledgeComponent(component.id)}
                  aria-label={`Remove ${component.displayName}`}
                >
                  <Dismiss20Regular className={styles.removeTagIcon} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Available Knowledge Components
          </h3>
        </div>
        {isLoadingKnowledgeComponents ? (
          <div className={styles.loadingContainer}>
            <Spinner label="Loading knowledge components..." size="medium" />
          </div>
        ) : (
          <div className={styles.accordionContainer}>
            <Accordion multiple collapsible>
              {groupedComponents.map((group) => {
                const selectedCount = group.items.filter((item) =>
                  selectedKnowledgeComponents.some(
                    (selected) => selected.id === item.id,
                  ),
                ).length;
                return (
                  <AccordionItem
                    key={group.categoryName}
                    value={group.categoryName}
                  >
                    <AccordionHeader className={styles.accordionHeader}>
                      <span className={styles.accordionHeaderContent}>
                        {group.categoryName}
                        {selectedCount > 0 && (
                          <Badge
                            className={mergeClasses(
                              badgeStyles.badge,
                              styles.tabBadgeMargin,
                            )}
                            appearance="filled"
                            color="brand"
                          >
                            {selectedCount}
                          </Badge>
                        )}
                      </span>
                    </AccordionHeader>
                    <AccordionPanel className={styles.accordionPanel}>
                      <div className={styles.availableItemsList}>
                        {group.items.map((component) => {
                          const isSelected = selectedKnowledgeComponents.some(
                            (selected) => selected.id === component.id,
                          );
                          return (
                            <div
                              key={component.id}
                              className={mergeClasses(
                                styles.selectableItem,
                                isSelected && styles.selectableItemSelected,
                              )}
                              onClick={() =>
                                handleToggleKnowledgeComponent(component)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleToggleKnowledgeComponent(component);
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              aria-pressed={isSelected}
                            >
                              <DocumentText20Regular />
                              <div className={styles.selectableItemContent}>
                                <div>
                                  <div className={styles.selectableItemText}>
                                    {component.name}
                                  </div>
                                </div>
                                {isSelected ? (
                                  <Checkmark12Regular
                                    className={styles.checkedMark}
                                  />
                                ) : (
                                  <Add12Regular className={styles.addIcon} />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </div>
    </>
  );
};

const WorkspaceDocumentsTab: React.FC<ITabContentProps> = ({
  workspaceDocuments,
  selectedWorkspaceDocuments,
  onWorkspaceDocumentsChange,
  workspaceId,
  onWorkspaceDocumentsRefresh,
  isLoadingWorkspaceDocuments,
}) => {
  const styles = useManageContentStyles();
  const sharedTagStyles = useSharedTagStyles();
  const sectionStyles = useSharedSectionStyles();
  const textStyles = useSharedTextStyles();
  const closeButtonStyles = useSharedCloseButtonStyles();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const dispatch = useDispatch();

  const handleToggleWorkspaceDocument = useCallback(
    (document: IWorkspaceLibraryDocument) => {
      const isSelected = selectedWorkspaceDocuments.some(
        (selected) => selected.filename === document.filename,
      );

      if (isSelected) {
        onWorkspaceDocumentsChange(
          selectedWorkspaceDocuments.filter(
            (selected) => selected.filename !== document.filename,
          ),
        );
      } else {
        onWorkspaceDocumentsChange([...selectedWorkspaceDocuments, document]);
      }
    },
    [selectedWorkspaceDocuments, onWorkspaceDocumentsChange],
  );

  const handleRemoveWorkspaceDocument = useCallback(
    (filename: string) => {
      onWorkspaceDocumentsChange(
        selectedWorkspaceDocuments.filter((doc) => doc.filename !== filename),
      );
    },
    [selectedWorkspaceDocuments, onWorkspaceDocumentsChange],
  );

  const formatUploadDate = (timestamp?: number) => {
    if (!timestamp) return "Unknown date";
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const handleAddDocument = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files: File[] = event.target.files
      ? Array.from(event.target.files)
      : [];
    if (!files.length || !workspaceId) return;

    setIsUploading(true);
    const allowedExtensions = ALLOWED_FILE_EXTENSIONS.map((ext) =>
      ext.toLowerCase(),
    );

    try {
      for (const file of files) {
        const fileExtension = file.name.split(".").pop()?.toLowerCase();

        if (fileExtension === "pptx" || fileExtension === "ppt") {
          dispatch(
            showToast(
              "PPT/PPTX files are not supported. Please save the file as a PDF and then upload the PDF.",
              "warning",
            ),
          );
          setIsUploading(false);
          return;
        }

        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
          dispatch(
            showToast(
              `The file "${file.name}" is not supported. Only these file types are allowed: ${ALLOWED_FILE_EXTENSIONS.join(", ")}`,
              "warning",
            ),
          );
          setIsUploading(false);
          return;
        }

        const uploadedFiles = await uploadFilesToStorage(
          [{ id: Date.now().toString(), file }],
          "workspace_library",
          workspaceId,
          false,
        );

        if (uploadedFiles.length > 0) {
          const blobFilename =
            uploadedFiles[0].url?.split("/").pop() ||
            ConstantValues.EMPTY_STRING;
          await addWorkspaceLibraryDocument({
            workspaceId: workspaceId,
            filename: blobFilename,
            originalFilename: file.name,
          });
        }
      }

      if (onWorkspaceDocumentsRefresh) {
        await onWorkspaceDocumentsRefresh();
      }
    } catch (error) {
      console.error("Failed to upload document:", error);
      dispatch(
        showToast("Failed to upload document. Please try again.", "error"),
      );
    } finally {
      setIsUploading(false);
      event.target.value = ConstantValues.EMPTY_STRING;
    }
  };

  return (
    <>
      <span
        className={styles.visuallyHidden}
        aria-live="polite"
        data-testid="upload-progress-live-region"
      >
        {isUploading ? "Uploading document..." : ""}
      </span>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={ALLOWED_FILE_EXTENSIONS.map(
          (extension: string) => `.${extension.toLowerCase()}`,
        ).join(",")}
        onChange={handleFileInputChange}
        multiple
      />

      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Selected Workspace Documents
          </h3>
        </div>
        <div className={styles.selectedItemsContainer}>
          {selectedWorkspaceDocuments.length === 0 ? (
            <span className={styles.emptySelectedState}>
              No workspace documents selected
            </span>
          ) : (
            selectedWorkspaceDocuments.map((document) => (
              <div
                key={document.filename}
                className={mergeClasses(
                  sharedTagStyles.tag,
                  sharedTagStyles.tagAccent,
                )}
              >
                <DocumentText16Regular
                  className={styles.selectedTagIconLarge}
                />
                <span className={styles.tagNameWithAttachments}>
                  {document.originalFilename}
                  <Tooltip content="Download document" relationship="label">
                    <Link
                      onClick={(e) => {
                        e.stopPropagation();
                        if (workspaceId) {
                          void downloadWorkspaceLibraryDocument(
                            workspaceId,
                            document,
                          );
                        }
                      }}
                      aria-label={`Download ${document.originalFilename}`}
                      className={styles.downloadLink}
                    >
                      <ArrowDownload16Regular />
                    </Link>
                  </Tooltip>
                </span>
                <button
                  className={mergeClasses(
                    closeButtonStyles.closeButton,
                    styles.removeButton,
                  )}
                  onClick={() =>
                    handleRemoveWorkspaceDocument(document.filename)
                  }
                  aria-label={`Remove ${document.originalFilename}`}
                >
                  <Dismiss20Regular className={styles.removeTagIcon} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
            style={{ marginBottom: "0" }}
          >
            Available Workspace Documents
          </h3>
          <Tooltip
            content="Add documents to the current workspace. Once added, they can be selected in any chat within the workspace."
            relationship="label"
            showDelay={300}
            hideDelay={100}
          >
            <Button
              className={styles.addButton}
              icon={isUploading ? <Spinner size="tiny" /> : <Add16Regular />}
              appearance="outline"
              onClick={handleAddDocument}
              disabled={isUploading}
              aria-label="Add documents to the current workspace. Once added, they can be selected in any chat within the workspace."
            >
              Add Document
            </Button>
          </Tooltip>
        </div>
        {isLoadingWorkspaceDocuments ? (
          <div className={styles.loadingContainer}>
            <Spinner label="Loading workspace documents..." size="medium" />
          </div>
        ) : (
          <div className={styles.availableItemsList}>
            {workspaceDocuments.map((document) => {
              const isSelected = selectedWorkspaceDocuments.some(
                (selected) => selected.filename === document.filename,
              );
              return (
                <div
                  key={document.filename}
                  className={mergeClasses(
                    styles.selectableItem,
                    isSelected && styles.selectableItemSelected,
                  )}
                  onClick={() => handleToggleWorkspaceDocument(document)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleToggleWorkspaceDocument(document);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-pressed={isSelected}
                >
                  <div className={styles.selectableItemContent}>
                    <div className={styles.documentItemContent}>
                      <DocumentText20Regular
                        className={styles.documentIconLarge}
                      />
                      <div className={styles.documentDetails}>
                        <div className={styles.selectableItemText}>
                          {document.originalFilename}
                        </div>
                        <div className={textStyles.caption}>
                          Uploaded {formatUploadDate(document.uploadedAt)}
                        </div>
                      </div>
                    </div>
                    {isSelected ? (
                      <Checkmark12Regular className={styles.checkedMark} />
                    ) : (
                      <Add12Regular className={styles.addIcon} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

const UploadedDocumentsTab: React.FC<ITabContentProps> = ({
  uploadedDocuments,
  onUploadedDocumentsChange,
}) => {
  const styles = useManageContentStyles();
  const sharedTagStyles = useSharedTagStyles();
  const sectionStyles = useSharedSectionStyles();

  return (
    <div className={styles.section}>
      <div
        className={mergeClasses(
          sectionStyles.sectionHeader,
          styles.sectionHeader,
        )}
      >
        <h3
          className={mergeClasses(
            sectionStyles.sectionTitle,
            styles.sectionTitle,
          )}
        >
          Uploaded Documents
        </h3>
      </div>
      {uploadedDocuments.length === 0 ? (
        <div className={styles.emptySelectedState}>No documents uploaded</div>
      ) : (
        <div className={styles.availableItemsListForChat}>
          {uploadedDocuments.map((document) => (
            <div
              key={document.id}
              className={mergeClasses(
                sharedTagStyles.tag,
                sharedTagStyles.tagAccent,
              )}
            >
              <span className={styles.tagNameWithAttachments}>
                {document.filename}
                {document.url && (
                  <Tooltip content="Download document" relationship="label">
                    <Link
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadFile(document.url!)();
                      }}
                      aria-label={`Download ${document.filename}`}
                      className={styles.downloadLink}
                    >
                      <ArrowDownload16Regular />
                    </Link>
                  </Tooltip>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReferencedChatsTab: React.FC<
  ITabContentProps & { onScrollHandlerReady?: (handler: () => void) => void }
> = ({
  selectedConversations,
  onConversationsChange,
  workspaceId,
  onScrollHandlerReady,
}) => {
  const styles = useManageContentStyles();
  const sharedTagStyles = useSharedTagStyles();
  const sectionStyles = useSharedSectionStyles();
  const textStyles = useSharedTextStyles();
  const cardStyles = useSharedCardStyles();
  const closeButtonStyles = useSharedCloseButtonStyles();
  const contentEndRef = useRef<HTMLDivElement>(null);
  const PAGE_SIZE = 10;

  const {
    data: conversationsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery(
    ["referencedConversations", workspaceId],
    async ({ pageParam = 0 }) => {
      return await getConversationsWithCount({
        workspaceId: workspaceId,
        searchText: undefined,
        limit: PAGE_SIZE,
        offset: pageParam,
        ascending: false,
      });
    },
    {
      enabled: !!workspaceId,
      keepPreviousData: true,
      staleTime: 30_000,
      cacheTime: 300_000,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        if (
          !lastPage?.conversations ||
          lastPage.conversations.length < PAGE_SIZE
        ) {
          return undefined;
        }
        const nextOffset = pages.length * PAGE_SIZE;
        return nextOffset;
      },
    },
  );

  const availableConversations = useMemo(() => {
    return (
      conversationsData?.pages.flatMap((page) => page.conversations || []) ?? []
    );
  }, [conversationsData]);

  useEffect(() => {
    const checkScroll = () => {
      if (!contentEndRef.current || !hasNextPage || isFetchingNextPage) return;

      const rect = contentEndRef.current.getBoundingClientRect();
      const isVisible = rect.top <= window.innerHeight;

      if (isVisible) {
        fetchNextPage();
      }
    };

    if (onScrollHandlerReady) {
      onScrollHandlerReady(checkScroll);
    }

    return () => {
      if (onScrollHandlerReady) {
        onScrollHandlerReady(() => {});
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, onScrollHandlerReady]);

  const handleToggleConversation = useCallback(
    (conversation: IConversation) => {
      const isSelected = selectedConversations.some(
        (selected) => selected.id === conversation.id,
      );

      if (isSelected) {
        onConversationsChange(
          selectedConversations.filter(
            (selected) => selected.id !== conversation.id,
          ),
        );
      } else if (selectedConversations.length < MAX_REFERENCED_CONVERSATIONS) {
        onConversationsChange([...selectedConversations, conversation]);
      }
    },
    [selectedConversations, onConversationsChange],
  );

  const handleRemoveConversation = useCallback(
    (conversationId: string | undefined) => {
      if (!conversationId) return;
      onConversationsChange(
        selectedConversations.filter((conv) => conv.id !== conversationId),
      );
    },
    [selectedConversations, onConversationsChange],
  );

  return (
    <>
      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Selected Chats
          </h3>
        </div>
        <div className={styles.selectedItemsContainer}>
          {selectedConversations.length === 0 ? (
            <span className={styles.emptySelectedState}>
              No chats selected. You can select up to 3 chats.
            </span>
          ) : (
            selectedConversations.map((conversation) => (
              <div
                key={conversation.id}
                className={mergeClasses(
                  sharedTagStyles.tag,
                  sharedTagStyles.tagAccent,
                )}
              >
                <ChatRegular className={styles.selectedTagIconSmall} />
                <span>{conversation.title}</span>
                <button
                  className={mergeClasses(
                    closeButtonStyles.closeButton,
                    styles.removeButton,
                  )}
                  onClick={() => handleRemoveConversation(conversation.id)}
                  aria-label={`Remove ${conversation.title}`}
                >
                  <Dismiss20Regular className={styles.removeTagIcon} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Available Chats
          </h3>
        </div>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner label="Loading chats..." size="medium" />
          </div>
        ) : isError ? (
          <div className={styles.emptySelectedState}>
            Failed to load chats. Please try again.
          </div>
        ) : (
          <div className={styles.availableItemsList}>
            {availableConversations.length === 0 ? (
              <div className={styles.emptySelectedState}>
                No chats available
              </div>
            ) : (
              <>
                {availableConversations.map((conversation) => {
                  const isSelected = selectedConversations.some(
                    (selected) => selected.id === conversation.id,
                  );
                  const canSelect =
                    !isSelected &&
                    selectedConversations.length < MAX_REFERENCED_CONVERSATIONS;
                  const isDisabled = !isSelected && !canSelect;

                  const item = (
                    <div
                      key={conversation.id}
                      className={mergeClasses(
                        cardStyles.card,
                        styles.conversationItem,
                        isSelected && styles.conversationItemSelected,
                        isDisabled && styles.conversationItemDisabled,
                      )}
                      onClick={() =>
                        !isDisabled && handleToggleConversation(conversation)
                      }
                      onKeyDown={(e) => {
                        if (
                          (e.key === "Enter" || e.key === " ") &&
                          !isDisabled
                        ) {
                          e.preventDefault();
                          handleToggleConversation(conversation);
                        }
                      }}
                      tabIndex={isDisabled ? -1 : 0}
                      role="button"
                      aria-pressed={isSelected}
                      aria-disabled={isDisabled}
                    >
                      <Chat20Regular className={styles.conversationIcon} />
                      <div className={styles.conversationInfo}>
                        <div className={styles.conversationTitle}>
                          {conversation.title}
                        </div>
                        <div
                          className={mergeClasses(
                            textStyles.caption,
                            styles.conversationLastMessage,
                          )}
                        >
                          Last updated: {formatDate(conversation.updatedAt)}
                        </div>
                      </div>
                      {isSelected ? (
                        <Checkmark12Regular className={styles.checkedMark} />
                      ) : (
                        <Add12Regular
                          className={mergeClasses(
                            styles.addIcon,
                            isDisabled && styles.addIconDisabled,
                          )}
                        />
                      )}
                    </div>
                  );

                  return isDisabled ? (
                    <Tooltip
                      key={conversation.id}
                      content={`Maximum of ${MAX_REFERENCED_CONVERSATIONS} conversations reached`}
                      relationship="description"
                      showDelay={300}
                      hideDelay={100}
                    >
                      {item}
                    </Tooltip>
                  ) : (
                    item
                  );
                })}
                {isFetchingNextPage && (
                  <div className={styles.loadingContainer}>
                    <Spinner label="Loading more..." size="small" />
                  </div>
                )}
                <div ref={contentEndRef} style={{ height: 1 }} />
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export const ManageContent: React.FC<IManageContentProps> = ({
  isOpen,
  isPersonalWorkspace,
  onClose,
  knowledgeComponents,
  selectedKnowledgeComponents,
  onKnowledgeComponentsChange,
  workspaceDocuments,
  selectedWorkspaceDocuments,
  onWorkspaceDocumentsChange,
  uploadedDocuments,
  onUploadedDocumentsChange,
  availableConversations,
  selectedConversations,
  onConversationsChange,
  workspaceId,
  onWorkspaceDocumentsRefresh,
  isLoadingKnowledgeComponents,
  isLoadingWorkspaceDocuments,
  isLoadingConversations,
}) => {
  const styles = useManageContentStyles();
  const sharedModalStyles = useSharedModalStyles();
  const badgeStyles = useSharedBadgeStyles();
  const buttonStyles = useSharedButtonStyles();
  const [activeTab, setActiveTab] = useState<string>(
    MANAGE_CONTENT_TABS.KNOWLEDGE_COMPONENTS,
  );
  const tabListRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<Element | null>(null);
  const tabContentRef = useRef<HTMLDivElement>(null);
  const referencedChatsScrollHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (
      activeTab === MANAGE_CONTENT_TABS.WORKSPACE_DOCUMENTS &&
      isPersonalWorkspace
    ) {
      setActiveTab(MANAGE_CONTENT_TABS.KNOWLEDGE_COMPONENTS);
    }
    if (
      activeTab === MANAGE_CONTENT_TABS.REFERENCED_CHATS &&
      isPersonalWorkspace
    ) {
      setActiveTab(MANAGE_CONTENT_TABS.KNOWLEDGE_COMPONENTS);
    }
  }, [activeTab, isPersonalWorkspace]);

  useEffect(() => {
    if (isOpen) {
      triggerElementRef.current = document.activeElement;
      modalContentRef.current?.focus();
    } else if (triggerElementRef.current instanceof HTMLElement) {
      triggerElementRef.current.focus();
      triggerElementRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const [tabsOverflow, setTabsOverflow] = useState(false);

  useEffect(() => {
    const el = tabListRef.current;
    if (!el || !isOpen) return;
    const check = () => setTabsOverflow(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    isOpen,
    selectedKnowledgeComponents.length,
    selectedWorkspaceDocuments.length,
    uploadedDocuments.length,
    selectedConversations.length,
  ]);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabListRef.current) {
      const scrollAmount = 150;
      const currentScroll = tabListRef.current.scrollLeft;
      const newScroll =
        direction === "left"
          ? Math.max(0, currentScroll - scrollAmount)
          : currentScroll + scrollAmount;

      tabListRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth",
      });
    }
  };

  const tabContentProps: ITabContentProps = {
    knowledgeComponents,
    selectedKnowledgeComponents,
    onKnowledgeComponentsChange,
    workspaceDocuments,
    selectedWorkspaceDocuments,
    onWorkspaceDocumentsChange,
    uploadedDocuments,
    onUploadedDocumentsChange,
    availableConversations,
    selectedConversations,
    onConversationsChange,
    workspaceId,
    onWorkspaceDocumentsRefresh,
    isLoadingKnowledgeComponents,
    isLoadingWorkspaceDocuments,
    isLoadingConversations,
  };

  const handleScrollHandlerReady = useCallback((handler: () => void) => {
    referencedChatsScrollHandlerRef.current = handler;
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case MANAGE_CONTENT_TABS.KNOWLEDGE_COMPONENTS:
        return <KnowledgeComponentsTab {...tabContentProps} />;
      case MANAGE_CONTENT_TABS.WORKSPACE_DOCUMENTS:
        return <WorkspaceDocumentsTab {...tabContentProps} />;
      case MANAGE_CONTENT_TABS.UPLOADED_DOCUMENTS:
        return <UploadedDocumentsTab {...tabContentProps} />;
      case MANAGE_CONTENT_TABS.REFERENCED_CHATS:
        return (
          <ReferencedChatsTab
            {...tabContentProps}
            onScrollHandlerReady={handleScrollHandlerReady}
          />
        );
      default:
        return <KnowledgeComponentsTab {...tabContentProps} />;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        className={mergeClasses(
          sharedModalStyles.overlay,
          sharedModalStyles.overlayActive,
        )}
        onClick={onClose}
      >
        <div
          ref={modalContentRef}
          className={mergeClasses(
            sharedModalStyles.modal,
            sharedModalStyles.modalActive,
            styles.modalContentOverride,
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="manage-content-title"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <header
            className={mergeClasses(
              sharedModalStyles.modalHeader,
              styles.modalHeaderOverride,
            )}
          >
            <h2
              id="manage-content-title"
              className={sharedModalStyles.modalTitle}
            >
              Content Sources
            </h2>
            <Button
              ref={closeButtonRef}
              icon={<Dismiss20Regular />}
              appearance="subtle"
              onClick={onClose}
              aria-label="Close Content Sources dialog"
            />
          </header>

          <nav className={styles.tabNavigation}>
            {tabsOverflow && (
              <Button
                icon={<ChevronLeft20Regular />}
                appearance="subtle"
                onClick={() => scrollTabs("left")}
                className={mergeClasses(
                  buttonStyles.ghostButton,
                  styles.tabScrollButton,
                  styles.tabScrollButtonLeft,
                )}
                aria-label="Scroll tabs left"
              />
            )}

            <div
              ref={tabListRef}
              className={mergeClasses(
                styles.tabListContainer,
                tabsOverflow && styles.tabListContainerOverflow,
              )}
            >
              <TabList
                selectedValue={activeTab}
                onTabSelect={(_, data) => setActiveTab(data.value as string)}
                className={styles.tabList}
              >
                <Tab
                  value={MANAGE_CONTENT_TABS.KNOWLEDGE_COMPONENTS}
                  className={styles.tab}
                >
                  <div className={styles.tabContent}>
                    <DocumentText16Regular />
                    <span>Knowledge Components</span>

                    {selectedKnowledgeComponents.length > 0 && (
                      <Badge
                        className={mergeClasses(
                          badgeStyles.badge,
                          styles.tabBadgeMargin,
                        )}
                        appearance="filled"
                        color="brand"
                      >
                        {selectedKnowledgeComponents.length}
                      </Badge>
                    )}
                  </div>
                </Tab>
                {isPersonalWorkspace ? (
                  <></>
                ) : (
                  <Tab
                    value={MANAGE_CONTENT_TABS.WORKSPACE_DOCUMENTS}
                    className={styles.tab}
                  >
                    <div className={styles.tabContent}>
                      <DocumentFolder16Regular />
                      <span>Workspace Documents</span>
                      {selectedWorkspaceDocuments.length > 0 && (
                        <Badge
                          className={mergeClasses(
                            badgeStyles.badge,
                            styles.tabBadgeMargin,
                          )}
                          appearance="filled"
                          color="brand"
                        >
                          {selectedWorkspaceDocuments.length}
                        </Badge>
                      )}
                    </div>
                  </Tab>
                )}

                <Tab
                  value={MANAGE_CONTENT_TABS.UPLOADED_DOCUMENTS}
                  className={styles.tab}
                >
                  <div className={styles.tabContent}>
                    <ArrowUpload16Regular />
                    <span>Uploaded Documents</span>

                    {uploadedDocuments.length > 0 && (
                      <Badge
                        className={mergeClasses(
                          badgeStyles.badge,
                          styles.tabBadgeMargin,
                        )}
                        appearance="filled"
                        color="brand"
                      >
                        {uploadedDocuments.length}
                      </Badge>
                    )}
                  </div>
                </Tab>
                {isPersonalWorkspace ? (
                  <></>
                ) : (
                  <Tab
                    value={MANAGE_CONTENT_TABS.REFERENCED_CHATS}
                    className={styles.tab}
                  >
                    <div className={styles.tabContent}>
                      <Chat16Regular />
                      <span>Referenced Chats</span>
                      {selectedConversations.length > 0 && (
                        <Badge
                          className={mergeClasses(
                            badgeStyles.badge,
                            styles.tabBadgeMargin,
                          )}
                          appearance="filled"
                          color="brand"
                        >
                          {selectedConversations.length}
                        </Badge>
                      )}
                    </div>
                  </Tab>
                )}
                <div className={styles.tabSpacer}></div>
              </TabList>
            </div>

            {tabsOverflow && (
              <Button
                icon={<ChevronRight20Regular />}
                appearance="subtle"
                onClick={() => scrollTabs("right")}
                className={mergeClasses(
                  buttonStyles.ghostButton,
                  styles.tabScrollButton,
                )}
                aria-label="Scroll tabs right"
              />
            )}
          </nav>

          <main
            ref={tabContentRef}
            className={styles.tabContentArea}
            onScroll={() => {
              if (
                activeTab === MANAGE_CONTENT_TABS.REFERENCED_CHATS &&
                referencedChatsScrollHandlerRef.current
              ) {
                referencedChatsScrollHandlerRef.current();
              }
            }}
          >
            {renderTabContent()}
          </main>

          <footer className={sharedModalStyles.modalFooter}>
            <Button
              className={styles.doneButton}
              appearance="primary"
              onClick={onClose}
            >
              Done
            </Button>
          </footer>
        </div>
      </div>
    </>
  );
};
